import { Ref } from 'preact';
import { forwardRef } from 'preact/compat';

import { CheckIcon } from '../PRIVATE_ThemedIcons/CheckIcon';
import { CheckboxOffIcon } from '../PRIVATE_ThemedIcons/CheckboxOffIcon';
import { CheckboxOnIcon } from '../PRIVATE_ThemedIcons/CheckboxOnIcon';
import { CheckboxMixedIcon } from '../PRIVATE_ThemedIcons/CheckboxMixedIcon';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { checkboxIconVars } from '../UNSAFE_Checkbox/themes/CheckboxIconContract.css';
import { CheckboxIconRedwoodTheme } from '../UNSAFE_Checkbox/themes/redwood/CheckboxIconTheme';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import {
  CheckboxIconStyles,
  CheckboxIconVariantOptions
} from '../UNSAFE_Checkbox/themes/CheckboxIconStyles.css';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type InputProps = HTMLAttributesSignalExcluded<HTMLInputElement>;

type Props = Pick<
  InputProps,
  | 'name'
  | 'value'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'onKeyUp'
  | 'onKeyDown'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'id'
> & {
  /**
   * Specifies if the checkbox is active.
   */
  isActive?: boolean;
  /**
   * Whether the element should be checked, unchecked, or in a partial state.
   */
  isChecked?: 'checked' | 'unchecked' | 'partial';
  /**
   * Specifies if the checkbox is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the checkbox should display focus ring.
   */
  isFocusRingShown?: boolean;
  /**
   * Specifies if the checkbox is read-only.
   */
  isReadonly?: boolean;
  /**
   * Specifies whether the checkbox requires a selection to be made.
   */
  isRequired?: boolean;
  /**
   * Triggered when the checkbox icon is clicked. This would be potentially used if no label is supplied.
   */
  onClick?: HTMLAttributesSignalExcluded<HTMLSpanElement>['onClick'];
};

/**
 * StyledCheckbox is a controlled component that displays a stylized checkbox for 'checked',
 * 'unchecked', and 'partial'. It passes intrinsic props to the native input element. This should be used
 * in conjunction with a label and be passed an onChange handler. If used within a collection use
 * 'useCollectionFocusRing', or use 'useCheckboxRadioContext' within RadioSet/CheckboxSet, to determine if you
 * should display the focus ring using keyboard navigation.
 */
const StyledCheckbox = forwardRef(
  (
    {
      onClick,
      isActive,
      isFocusRingShown,
      isDisabled,
      isReadonly,
      isRequired,
      isChecked: propIsChecked = 'unchecked',
      // Remaining props should be intrinsic input props only. If you need additional input props exposed,
      // add to the picked items from InputProps.
      id,
      name,
      value,
      onKeyUp,
      onKeyDown,
      onChange,
      onFocus,
      onBlur,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy
    }: Props,
    ref: Ref<HTMLInputElement> = null
  ) => {
    const { isTabbable } = useTabbableMode();
    const isPartial = propIsChecked === 'partial';
    const isChecked = propIsChecked === 'checked' || isPartial;
    const ariaChecked = isChecked ? (isPartial ? 'mixed' : true) : false;

    const { classes } = useComponentTheme<CheckboxIconVariantOptions, CheckboxIconStyles>(
      CheckboxIconRedwoodTheme,
      {
        disabled: isDisabled ? 'isDisabled' : 'notDisabled',
        checked: isChecked ? 'isChecked' : 'notChecked',
        readonly: isReadonly ? 'isReadonly' : 'notReadonly',
        active: isActive ? 'isActive' : 'notActive',
        focus: isFocusRingShown ? 'isFocused' : 'notFocused',
        iconSize: '4xUnits'
      }
    );

    const IconComp = isReadonly
      ? isChecked
        ? CheckIcon
        : CheckboxOffIcon
      : isChecked
      ? isPartial
        ? CheckboxMixedIcon
        : CheckboxOnIcon
      : CheckboxOffIcon;

    return (
      <>
        <HiddenAccessible>
          <input
            {...(isReadonly ? { 'aria-readonly': true } : {})}
            aria-checked={ariaChecked}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-required={isRequired}
            ref={ref}
            name={name}
            value={value}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            id={id}
            type="checkbox"
            disabled={isDisabled}
            checked={isChecked}
            tabIndex={isTabbable ? 0 : -1}
          />
        </HiddenAccessible>
        <span className={classes} aria-hidden="true" onClick={onClick} role="img">
          <IconComp size={checkboxIconVars.iconSize} color="currentColor" />
        </span>
      </>
    );
  }
);

export { StyledCheckbox };
